/**
 * Service for handling academic user expiration workflow
 */

const ACADEMIC_API_URL =
  process.env.REACT_APP_ACADEMIC_API_URL || 'https://o0vnudbmyc.execute-api.eu-central-1.amazonaws.com';

// Deployment date: February 16, 2025 - grace period starts from this date
const DEPLOYMENT_DATE = new Date('2025-02-16T00:00:00Z');
const GRACE_PERIOD_DAYS = 30;

export interface AcademicExpirationStatus {
  isExpired: boolean;
  daysUntilExpiration: number;
  expirationDate: Date;
  canRenew: boolean;
  hasGracePeriod: boolean;
  gracePeriodDaysRemaining: number;
}

export interface AcademicRenewalResponse {
  success: boolean;
  message: string;
  error?: string;
}

class AcademicExpirationService {
  /**
   * Calculate academic expiration status based on user creation date and renewal date
   * If user has been renewed or upgraded, use the most recent date
   */
  calculateExpirationStatus(
    userCreatedAt: string, 
    userRenewedAt?: string, 
    nextPaymentDue?: string,
    notifiedAt?: string
  ): AcademicExpirationStatus {
    const createdAtTimestamp = parseInt(userCreatedAt);
    const renewedAtTimestamp = userRenewedAt ? parseInt(userRenewedAt) : null;

    let expirationDate: Date;
    
    if (nextPaymentDue) {
      expirationDate = new Date(nextPaymentDue);
    } else {
      let baseDate: Date;
      
      if (renewedAtTimestamp) {
        baseDate = new Date(renewedAtTimestamp);
      } else {
        baseDate = new Date(createdAtTimestamp);
      }

      expirationDate = new Date(baseDate);
      expirationDate.setFullYear(expirationDate.getFullYear() + 1);
    }

    const now = new Date();
    const timeDiff = expirationDate.getTime() - now.getTime();
    const daysUntilExpiration = Math.ceil(timeDiff / (1000 * 3600 * 24));

    const isExpired = daysUntilExpiration <= 0;

    let gracePeriodDaysRemaining = 0;
    let hasGracePeriod = false;

    if (isExpired) {
      const gracePeriodEnd = new Date(DEPLOYMENT_DATE);
      gracePeriodEnd.setDate(gracePeriodEnd.getDate() + GRACE_PERIOD_DAYS);
      
      const timeUntilGraceEnd = gracePeriodEnd.getTime() - now.getTime();
      gracePeriodDaysRemaining = Math.ceil(timeUntilGraceEnd / (1000 * 3600 * 24));
      hasGracePeriod = gracePeriodDaysRemaining > 0;
    }

    return {
      isExpired,
      daysUntilExpiration: Math.max(0, daysUntilExpiration),
      expirationDate,
      canRenew: true,
      hasGracePeriod,
      gracePeriodDaysRemaining: Math.max(0, gracePeriodDaysRemaining),
    };
  }

  /**
   * Check if user should see expiration warning
   */
  shouldShowWarning(expirationStatus: AcademicExpirationStatus): boolean {
    const { isExpired, daysUntilExpiration } = expirationStatus;

    // Show warning if expired (regardless of grace period)
    if (isExpired) {
      return true;
    }

    // Show warning if 30 days or less until expiration
    if (!isExpired && daysUntilExpiration <= 30) {
      return true;
    }

    return false;
  }

  /**
   * Check if this is an existing user who was already expired (special case)
   * This handles users who were already expired before the new system was implemented
   */
  isExistingExpiredUser(userCreatedAt: string, userRenewedAt?: string): boolean {
    // If user has renewal date, they're not in the "existing expired" category
    if (userRenewedAt) return false;

    const createdAtTimestamp = parseInt(userCreatedAt);
    const createdDate = new Date(createdAtTimestamp);
    const now = new Date();

    // Check if user was created more than 365 days ago
    const daysSinceCreation = Math.floor((now.getTime() - createdDate.getTime()) / (1000 * 3600 * 24));
    return daysSinceCreation > 365;
  }

  /**
   * Initialize grace period for existing expired users
   */
  initializeGracePeriod(warningType: string): void {
    const key = `academic_grace_period_start_${warningType}`;
    const existingGracePeriod = localStorage.getItem(key);

    if (!existingGracePeriod) {
      localStorage.setItem(key, Date.now().toString());
    }
  }

  getGracePeriodInfo(warningType: string, notifiedAtTimestamp?: string): { daysRemaining: number; isInGracePeriod: boolean } {
    if (notifiedAtTimestamp) {
      const notifiedAtMs = parseInt(notifiedAtTimestamp);
      const notifiedAt = new Date(notifiedAtMs);
      const graceEnd = new Date(notifiedAt.getTime() + 30 * 24 * 60 * 60 * 1000); // 30 days from notification
      const now = new Date();

      const daysRemaining = Math.ceil((graceEnd.getTime() - now.getTime()) / (24 * 60 * 60 * 1000));
      const isInGracePeriod = daysRemaining > 0;

      return { daysRemaining: Math.max(0, daysRemaining), isInGracePeriod };
    }

    const key = `academic_grace_period_start_${warningType}`;
    const gracePeriodStart = localStorage.getItem(key);

    if (!gracePeriodStart) {
      return { daysRemaining: 30, isInGracePeriod: true };
    }

    const graceStart = new Date(parseInt(gracePeriodStart));
    const graceEnd = new Date(graceStart.getTime() + 30 * 24 * 60 * 60 * 1000); // 30 days
    const now = new Date();

    const daysRemaining = Math.ceil((graceEnd.getTime() - now.getTime()) / (24 * 60 * 60 * 1000));
    const isInGracePeriod = daysRemaining > 0;

    return { daysRemaining: Math.max(0, daysRemaining), isInGracePeriod };
  }

  /**
   * Get warning type based on expiration status
   */
  getWarningType(
    expirationStatus: AcademicExpirationStatus,
  ): 'expired' | 'expiring-soon' | 'critical' | null {
    const { isExpired, daysUntilExpiration, hasGracePeriod } = expirationStatus;

    // If expired, always show expired warning (regardless of grace period)
    if (isExpired) {
      return 'expired';
    }

    if (!isExpired && daysUntilExpiration <= 7) {
      return 'critical';
    }

    if (!isExpired && daysUntilExpiration <= 30) {
      return 'expiring-soon';
    }

    return null;
  }

  /**
   * Initiate academic renewal process
   */
  async renewAcademicAccess(email: string, formData?: any): Promise<AcademicRenewalResponse> {
    try {
      const requestBody: any = { email };
      
      if (formData) {
        requestBody.renewalFeedback = {
          mainReason: formData.mainReason,
          usageFrequency: formData.usageFrequency,
          usefulness: formData.usefulness,
          suggestions: formData.suggestions
        };
      }

      const response = await fetch(`${ACADEMIC_API_URL}/renew-academic`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(requestBody),
      });

      const data = await response.json();

      if (!response.ok) {
        return {
          success: false,
          message: data.error || 'Failed to initiate renewal process',
          error: data.error,
        };
      }

      return {
        success: true,
        message: data.message || 'Renewal application submitted successfully',
      };
    } catch (error) {
      console.error('Error renewing academic access:', error);
      return {
        success: false,
        message: 'An error occurred while processing your renewal request. Please try again.',
        error: error instanceof Error ? error.message : 'Unknown error',
      };
    }
  }

  /**
   * Cancel academic account (user chooses not to renew)
   */
  async cancelAcademicAccount(email: string): Promise<AcademicRenewalResponse> {
    try {
      const response = await fetch(`${ACADEMIC_API_URL}/cancel-academic`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email }),
      });

      const data = await response.json();

      if (!response.ok) {
        return {
          success: false,
          message: data.error || 'Failed to process cancellation',
          error: data.error,
        };
      }

      return {
        success: true,
        message: data.message || 'Account cancellation processed successfully',
      };
    } catch (error) {
      console.error('Error canceling academic account:', error);
      return {
        success: false,
        message: 'An error occurred while processing your cancellation. Please contact support.',
        error: error instanceof Error ? error.message : 'Unknown error',
      };
    }
  }

  /**
   * Dismiss renewal approval modal (removes renewal application tag)
   */
  async dismissRenewalApproval(email: string): Promise<AcademicRenewalResponse> {
    try {
      const response = await fetch(`${ACADEMIC_API_URL}/dismiss-renewal-approval`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email }),
      });

      const data = await response.json();

      if (!response.ok) {
        return {
          success: false,
          message: data.error || 'Failed to dismiss renewal approval',
          error: data.error,
        };
      }

      return {
        success: true,
        message: data.message || 'Renewal approval dismissed successfully',
      };
    } catch (error) {
      console.error('Error dismissing renewal approval:', error);
      return {
        success: false,
        message: 'An error occurred while dismissing renewal approval.',
        error: error instanceof Error ? error.message : 'Unknown error',
      };
    }
  }

  /**
   * Check if user has already been shown warning today
   */
  hasShownWarningToday(warningType: string): boolean {
    const key = `academic_warning_${warningType}_shown`;
    const lastShown = localStorage.getItem(key);

    if (!lastShown) return false;

    const lastShownDate = new Date(lastShown);
    const today = new Date();

    // Check if it's the same day
    return lastShownDate.toDateString() === today.toDateString();
  }

  /**
   * Mark warning as shown today
   */
  markWarningShown(warningType: string): void {
    const key = `academic_warning_${warningType}_shown`;
    localStorage.setItem(key, new Date().toISOString());
  }
}

const academicExpirationService = new AcademicExpirationService();
export default academicExpirationService;
